import FileDownload from 'js-file-download';
import { createAsyncAction, createStandardAction } from 'typesafe-actions';
import { parse } from 'date-fns';

import apiAction from 'actions/_shared/apiAction';
import { makeApiUrl } from 'actions/_shared/utils';
import {
  asyncReportJobDownloadUrl,
  asyncReportJobStatusUrl,
  clearAsyncReportJobPolling,
  getAsyncReportJobFailedMessage,
  getAsyncReportJobTimeoutMessage,
  isAsyncReportJobPollingTimedOut,
  notifyAsyncReportJobError,
  notifyAsyncReportJobProcessing,
  scheduleAsyncReportJobPoll,
} from 'actions/_shared/asyncReportJob';
import { authService } from 'services/AuthProvider';
import {
  SystemPageCardResponseType,
  ColorDataItem,
  UniversalSystemPageRequestType,
  UniversalSystemPageMesage,
  DayViewType,
  UserSystemGroupStrings,
} from 'store/storeTypes';
import { mapResposeToTimeLineDayArray } from 'helpers/timelineUtils';
import { getDayForDate } from 'helpers/date';
import { SetTimeLineHour, ToggleHour } from 'components/SystemPage/TimeLineChart/TimePicker';
import { getRealTimeHeaders } from './_shared/realTimeHeaders';
import { ISystemReport } from 'models/SystemAnalysisData';

export const toggleAMPMmodificator = createStandardAction('TOGGLE_AM_PM')<ToggleHour>();
export const setTimeLineHour = createStandardAction('SET_TIMELINE_HOUR')<SetTimeLineHour>();
export const incrementTimeLineHour = createStandardAction('INCREMENT_HOUR_TIMELINE')();
export const decrementTimeLineHour = createStandardAction('DECREMENT_HOUR_TIMELINE')();
export const toggleDayTimelineView = createStandardAction('TOGGLE_DAY_TIMELINE_VIEW')();
export const setTimeLineWeekSelectedDay = createStandardAction('SET_TIMELINE_WEEK_SELECTED_DAY')<
  string | null
>();
export const setActivePrinter = createStandardAction('SET_ACTIVE_PRINTER')<ISystemReport>();

const ERROR_MAINTENANCE_FILE_SEGMENT = 'error-maintenance-file';

export const downloadErrorAndMaintenanceReportAC = createAsyncAction(
  'DOWNLOAD_REPORT_STARTED',
  'DOWNLOAD_REPORT_SUCCESS',
  'DOWNLOAD_REPORT_FAILED'
)<GetSystemPageDataRequest, any, any>();

type MessagesCustomQueryParams = {
  custom?: string;
  startHour?: number;
  endHour?: number;
};

const normalizeCustomDateRange = (rawCustom: string): string => {
  const normalizedCustom = decodeURIComponent(rawCustom)
    .split('&')[0]
    .trim();
  return normalizedCustom.includes(' - ')
    ? normalizedCustom
    : `${normalizedCustom} - ${normalizedCustom}`;
};

const extractHourFromLegacyCustom = (
  rawCustom: string,
  key: 'startHour' | 'endHour'
): number | undefined => {
  const regex = new RegExp(`${key}=(\\d+)`);
  const match = decodeURIComponent(rawCustom).match(regex);
  return match ? Number(match[1]) : undefined;
};

const getMessagesCustomQueryParams = (
  custom: any,
  fallbackStartHour?: number,
  fallbackEndHour?: number
): MessagesCustomQueryParams => {
  if (typeof custom === 'string' && custom.trim()) {
    return {
      custom: normalizeCustomDateRange(custom),
      startHour: fallbackStartHour ?? extractHourFromLegacyCustom(custom, 'startHour'),
      endHour: fallbackEndHour ?? extractHourFromLegacyCustom(custom, 'endHour'),
    };
  }

  if (!custom || !custom.customTimeFrameStartDate || !custom.customTimeFrameEndDate) {
    return {};
  }

  const startDay = getDayForDate(custom.customTimeFrameStartDate).title;
  const endDay = getDayForDate(custom.customTimeFrameEndDate).title;
  return {
    custom: `${startDay} - ${endDay}`,
    startHour: custom.customTimeFrameStartHour,
    endHour: custom.customTimeFrameEndHour,
  };
};

type ErrorAndMaintenanceJob = {
  jobId: string;
  system: string;
  fileName: string;
  //* namespaced per report, otherwise exports of different reports for the same system
  //* would share a polling timer and cancel each other
  pollingKey: string;
  startedAt: number;
  attempt: number;
};

const getErrorAndMaintenanceJobPollingKey = (system: string) =>
  `${ERROR_MAINTENANCE_FILE_SEGMENT}:${system}`;

const failErrorAndMaintenanceJob = (
  dispatch,
  { pollingKey }: Partial<ErrorAndMaintenanceJob>,
  errorMessage?: string
) => {
  clearAsyncReportJobPolling(pollingKey);
  if (errorMessage) {
    notifyAsyncReportJobError(dispatch, errorMessage);
  }
  dispatch(downloadErrorAndMaintenanceReportAC.failure({ message: errorMessage }));
};

const downloadErrorAndMaintenanceJobFile = (job: ErrorAndMaintenanceJob) =>
  apiAction({
    request: {
      url: asyncReportJobDownloadUrl(job.jobId),
      params: { responseType: 'blob' },
    },
    logic: {
      onStarted: () => {},
      onFailed: (error, dispatch) => failErrorAndMaintenanceJob(dispatch, job),
      onSuccess: (data, dispatch, state, status) => {
        clearAsyncReportJobPolling(job.pollingKey);
        FileDownload(data, job.fileName);
        dispatch(downloadErrorAndMaintenanceReportAC.success({ status }));
      },
    },
  });

const pollErrorAndMaintenanceJobStatus = (job: ErrorAndMaintenanceJob) =>
  apiAction({
    request: {
      url: asyncReportJobStatusUrl(job.jobId),
    },
    logic: {
      onStarted: () => {},
      onFailed: (error, dispatch) => failErrorAndMaintenanceJob(dispatch, job),
      onSuccess: (data, dispatch) => {
        if (data?.status === 'Completed') {
          dispatch(downloadErrorAndMaintenanceJobFile(job));
          return;
        }
        if (data?.status === 'Failed') {
          failErrorAndMaintenanceJob(
            dispatch,
            job,
            data?.errorMessage || getAsyncReportJobFailedMessage()
          );
          return;
        }
        if (isAsyncReportJobPollingTimedOut(job.startedAt)) {
          failErrorAndMaintenanceJob(dispatch, job, getAsyncReportJobTimeoutMessage());
          return;
        }
        const nextJob = { ...job, attempt: job.attempt + 1 };
        notifyAsyncReportJobProcessing(dispatch, nextJob.attempt);
        scheduleAsyncReportJobPoll(nextJob.pollingKey, () =>
          dispatch(pollErrorAndMaintenanceJobStatus(nextJob))
        );
      },
    },
  });

export const downloadErrorAndMaintenanceReport = ({
  day,
  week,
  custom,
  startHour,
  endHour,
  systemGroup,
  system,
}: any) => {
  const url = makeApiUrl(`/api/system-page-report/${ERROR_MAINTENANCE_FILE_SEGMENT}/async`);
  const pollingKey = getErrorAndMaintenanceJobPollingKey(system);
  const customQueryParams = getMessagesCustomQueryParams(custom, startHour, endHour);
  const customDateRange = customQueryParams.custom;
  const customStartHour = customQueryParams.startHour;
  const customEndHour = customQueryParams.endHour;

  const getDateSuffix = () => {
    if (week) {
      return week;
    }
    if (day) {
      return day;
    }
    if (customDateRange) {
      if (
        customStartHour !== undefined &&
        customStartHour !== null &&
        customEndHour !== undefined &&
        customEndHour !== null
      ) {
        return `${customDateRange} ${customStartHour}-${customEndHour}h`;
      }
      return customDateRange;
    }
    return 'period';
  };

  return apiAction({
    request: {
      url,
      method: 'POST',
      params: {
        day,
        week,
        ...customQueryParams,
        systemGroup,
        system,
      },
    },
    logic: {
      onFailed: (error, dispatch) => {
        dispatch(
          downloadErrorAndMaintenanceReportAC.failure({
            name: error.name,
            message: error.message,
          })
        );
      },
      onStarted: dispatch => {
        clearAsyncReportJobPolling(pollingKey);
        dispatch(
          downloadErrorAndMaintenanceReportAC.request({
            day,
            week,
            custom,
            systemGroup,
            system,
          })
        );
      },
      onSuccess: (data, dispatch, state, status) => {
        if (status === 204 || status === 205) {
          dispatch(downloadErrorAndMaintenanceReportAC.success({ status }));
          return;
        }
        //*here we need to strip the site name prefix off the serial number, e.g. "Atlas 111-111-1" -> "111-111-1"
        const nameArray = system.split(' ');
        const serialNumber = nameArray[nameArray.length - 1];
        const dateSuffix = getDateSuffix();
        const job: ErrorAndMaintenanceJob = {
          jobId: data?.jobId,
          system,
          fileName: `${serialNumber}_Events_${dateSuffix}.xlsx`,
          pollingKey,
          startedAt: Date.now(),
          attempt: 0,
        };
        if (!job.jobId) {
          failErrorAndMaintenanceJob(dispatch, job, getAsyncReportJobFailedMessage());
          return;
        }
        notifyAsyncReportJobProcessing(dispatch, job.attempt);
        scheduleAsyncReportJobPoll(job.pollingKey, () =>
          dispatch(pollErrorAndMaintenanceJobStatus(job))
        );
      },
    },
  });
};

export type GetSystemPageDataRequest = {
  day?: string;
  week?: string;
  custom?: any;
  systemGroup: UserSystemGroupStrings;
  system: string;
  isProductionHours?: boolean;
};
export type DayObject = {
  date: Date;
  stringDate: string;
};

export const getTimeLineDataAC = createAsyncAction(
  'GET_TIMELINE_DATA_WEEK_VIEW_STARTED',
  'GET_TIMELINE_DATA_WEEK_VIEW_SUCCESS',
  'GET_TIMELINE_DATA_WEEK_VIEW_FAILED'
)<any, any, Error>();
export const getTimeLineDataRequest = ({
  day,
  week,
  systemGroup,
  system,
  isProductionHours,
}: GetSystemPageDataRequest) => {
  const url = makeApiUrl('/api/system-page-report/system-timeline');
  return apiAction({
    request: {
      url,
      params: {
        day,
        week,
        systemGroup,
        system,
        isProductionHours,
      },
    },
    logic: {
      onFailed: (error, dispatch) => {
        dispatch(getTimeLineDataAC.failure({ name: error.name, message: error.message }));
      },
      onStarted: dispatch => {
        dispatch(getTimeLineDataAC.request({ day, week, systemGroup, system }));
      },
      onSuccess: (data, dispatch) => {
        //*week req
        const {
          resultSummaryDataArray,
          resultTimeLineDataArray,
        } = mapTimeLineResponseToResultSortedObject(data, week);
        dispatch(getTimeLineDataAC.success({ resultSummaryDataArray, resultTimeLineDataArray }));
      },
    },
  });
};
const sortArrayByDay = arr => {
  return arr.sort((a, b) => {
    const dateA = parse(a.day, 'yyyyMMdd', new Date()).getTime();
    const dateB = parse(b.day, 'yyyyMMdd', new Date()).getTime();

    return dateA - dateB;
  });
};

export function mapTimeLineResponseToResultSortedObject(data, week) {
  const isWeek = week ? true : false;
  const { systemTimeLineGraphResponse, systemTimeLineSummeryResponse } = data;
  const { systemTimeLineGraphData: dataArray } = systemTimeLineGraphResponse;
  const { systemTimeLineSummeryData: dataSummaryArray } = systemTimeLineSummeryResponse;
  const resultSummaryDataArray = new Array();
  const resultTimeLineDataArray = new Array();

  dataSummaryArray.forEach(element => {
    const statusName = element.status;
    const position = timeLineSortingObject.find(el => el.name === statusName).positionInOrder - 1;
    resultSummaryDataArray.splice(position, 0, element);
  });
  if (isWeek) {
    const sortedArray = sortArrayByDay(dataArray);
    const firstDay: string = sortedArray[0].day;
    const { startYear, startMonth, startDay, startDate } = mapStringToDate(firstDay);
    const allWeekDaysStringArray = getAllWeekDaysObjects(startDate, firstDay);
    allWeekDaysStringArray.forEach((dayObject: DayObject) => {
      const filteredByDate = sortedArray.filter(el => el.day === dayObject.stringDate);
      const result = {
        date: dayObject.date,
        stringDate: dayObject.stringDate,
        Error:
          (filteredByDate.find(el => el.status === 'Error') &&
            filteredByDate.find(el => el.status === 'Error').statusDurationPercentage) ||
          0,
        Maintenance:
          (filteredByDate.find(el => el.status === 'Maintenance') &&
            filteredByDate.find(el => el.status === 'Maintenance').statusDurationPercentage) ||
          0,
        Idle:
          (filteredByDate.find(el => el.status === 'Idle') &&
            filteredByDate.find(el => el.status === 'Idle').statusDurationPercentage) ||
          0,
        Printing:
          (filteredByDate.find(el => el.status === 'Printing') &&
            filteredByDate.find(el => el.status === 'Printing').statusDurationPercentage) ||
          0,
        NotReporting:
          (filteredByDate.find(el => el.status === 'Not Reporting') &&
            filteredByDate.find(el => el.status === 'Not Reporting').statusDurationPercentage) ||
          0,
      };
      resultTimeLineDataArray.push(result);
    });
  }
  return { resultSummaryDataArray, resultTimeLineDataArray };
}
export function getAllWeekDaysObjects(startDate: Date, firstDay: string) {
  const resultArray = new Array();
  resultArray.push({ date: startDate, stringDate: firstDay });
  for (let i = 1; i <= 6; i++) {
    const year = startDate.getFullYear();
    const month = startDate.getMonth();
    const day =
      startDate.getDate() + i < 10 ? `0${startDate.getDate() + i}` : `${startDate.getDate() + i}`;

    const nextDate = new Date(year, month, +day);
    const nextMonth = nextDate.getMonth() + 1;
    const monthString = nextMonth < 10 ? `0${nextMonth}` : nextMonth;
    const nextDay = nextDate.getDate();
    const nextDayString = nextDay < 10 ? `0${nextDay}` : nextDay;
    resultArray.push({
      date: nextDate,
      stringDate: `${nextDate.getFullYear()}${monthString}${nextDayString}`,
    });
  }
  return resultArray;
}
export function mapStringToDate(date: string) {
  const splittedDate = date.split('');
  const startYear = Number(splittedDate.slice(0, 4).join(''));
  const startMonth = Number(splittedDate.slice(4, 6).join(''));
  const startDay = Number(splittedDate.slice(6, splittedDate.length).join(''));
  const startDate = new Date(startYear, startMonth - 1, startDay);
  return { startYear, startMonth, startDay, startDate };
}
export const getSystemPageCardsDataAC = createAsyncAction(
  'GET_SYSTEM_PAGE_CARDS_STARTED',
  'GET_SYSTEM_PAGE_CARDS_SUCCESS',
  'GET_SYSTEM_PAGE_CARDS_FAILED'
)<UniversalSystemPageRequestType, SystemPageCardResponseType, Error>();

export const getSystemPageCardsDataRequest = ({
  day,
  week,
  systemGroup,
  system,
  isProductionHours,
}: UniversalSystemPageRequestType) => {
  const url = makeApiUrl('/api/system-page-report/cards');
  return apiAction({
    request: {
      url,
      params: {
        day,
        week,
        systemGroup,
        system,
        isProductionHours,
      },
    },
    logic: {
      onFailed: (error, dispatch) => {
        dispatch(getSystemPageCardsDataAC.failure({ name: error.name, message: error.message }));
      },
      onStarted: dispatch => {
        dispatch(getSystemPageCardsDataAC.request({ day, week, systemGroup, system }));
      },
      onSuccess: (data, dispatch) => {
        const {
          achievement,
          productionRateChangeLastWeek,
          productionRateChangeFleet,
          productionRate,
          productionChangeLastWeek,
          productionChangeFleet,
          production,
          printerImage,
          lastStatus,
          lastSoftwareUpdate,
          availabilityChangeLastWeek,
          availabilityChangeFleet,
          availability,
          appVersion,
          errorDuration,
          errorPercentage,
          maintenanceDuration,
          maintenancePercentage,
          productivity,
          productivityChangeFleet,
          productivityChangeLastWeek,
          isNotReportingPeriod,
          availabilityTotalDurationReported,
          systemModel,
        } = data;
        dispatch(
          getSystemPageCardsDataAC.success({
            achievement,
            productionRateChangeLastWeek,
            productionRateChangeFleet,
            productionRate,
            productionChangeLastWeek,
            productionChangeFleet,
            production,
            printerImage,
            lastStatus,
            lastSoftwareUpdate,
            availabilityChangeLastWeek,
            availabilityChangeFleet,
            availability,
            appVersion,
            errorDuration,
            errorPercentage,
            maintenanceDuration,
            maintenancePercentage,
            productivity,
            productivityChangeFleet,
            productivityChangeLastWeek,
            isNotReportingPeriod,
            availabilityTotalDurationReported,
            systemModel,
          })
        );
      },
    },
  });
};
export type SystemPageMessagesResponseType = {
  maintenanceTableData: {
    messageTableData: Array<UniversalSystemPageMesage>;
  };
  errorTableData: {
    messageTableData: Array<UniversalSystemPageMesage>;
  };
};
export const getSystemPageMessagesAC = createAsyncAction(
  'GET_SYSTEM_PAGE_MESSAGES_STARTED',
  'GET_SYSTEM_PAGE_MESSAGES_SUCCESS',
  'GET_SYSTEM_PAGE_MESSAGES_FAILED'
)<UniversalSystemPageRequestType, any, Error>();

function reorderToPutOtherAtTheEnd(arr: any[]) {
  const otherItem = arr.find(item => item.displayMessage === 'Other');
  if (!otherItem) return arr;
  const withoutOther = arr.filter(item => item.displayMessage !== 'Other');
  withoutOther.push(otherItem);
  return withoutOther;
}

export const getSystemPageMessagesRequest = (
  { day, week, custom, systemGroup, system, isProductionHours }: UniversalSystemPageRequestType,
  isSilent,
  isRealTime = false
) => {
  const url = makeApiUrl('/api/system-page-report/error-maintenance-messages');
  const customQueryParams = getMessagesCustomQueryParams(custom);
  return apiAction({
    request: {
      url,
      headers: getRealTimeHeaders(isRealTime),
      params: {
        day,
        week,
        ...customQueryParams,
        systemGroup,
        system,
        isProductionHours,
      },
    },
    logic: {
      onFailed: (error, dispatch) => {
        dispatch(getSystemPageMessagesAC.failure({ name: error.name, message: error.message }));
      },
      onStarted: dispatch => {
        dispatch(getSystemPageMessagesAC.request({ day, week, systemGroup, system, isSilent }));
      },
      onSuccess: (data, dispatch) => {
        data.maintenanceTableData.messageTableData = reorderToPutOtherAtTheEnd(
          data.maintenanceTableData.messageTableData
        );
        data.errorTableData.messageTableData = reorderToPutOtherAtTheEnd(
          data.errorTableData.messageTableData
        );
        dispatch(getSystemPageMessagesAC.success(data));
      },
    },
  });
};

export const getTotalBottlesLoadedAC = createAsyncAction(
  'GET_TOTAL_BOTTLES_LOADED_STARTED',
  'GET_TOTAL_BOTTLES_LOADED_SUCCESS',
  'GET_TOTAL_BOTTLES_LOADED_FAILED'
)<UniversalSystemPageRequestType, Array<ColorDataItem>, Error>();
export const totalBottlesLoadedDataRequest = (
  { day, week, systemGroup, system, isProductionHours }: UniversalSystemPageRequestType,
  isSilent
) => {
  const url = makeApiUrl('/api/system-page-report/ink-table');
  const isToday = !!(day && isSilent);
  return apiAction({
    request: {
      url,
      headers: getRealTimeHeaders(isSilent),
      params: {
        day,
        week,
        systemGroup,
        system,
        isToday: isToday,
        isProductionHours,
      },
    },
    logic: {
      onFailed: (error, dispatch) => {
        dispatch(getTotalBottlesLoadedAC.failure({ name: error.name, message: error.message }));
      },
      onStarted: dispatch => {
        dispatch(getTotalBottlesLoadedAC.request({ day, week, systemGroup, system, isSilent }));
      },
      onSuccess: (data, dispatch) => {
        const { colorData } = data;
        //*there is a bug on backend side - sometimes we got negative values for for colorValue - there is no possibility to fix it on back end site so we need to handle it here. bug number 138641
        //* also contract has changed now insead of numbers also possible to get 'Infinity' string - should be mapped to 0
        const normalizedData = setAllNegativeValuesOfArrayToZero(colorData);
        dispatch(getTotalBottlesLoadedAC.success(normalizedData));
      },
    },
  });
};

export const getQualisetDataAC = createAsyncAction(
  'GET_QUALISET_DATA_STARTED',
  'GET_QUALISET_DATA_SUCCESS',
  'GET_QUALISET_DATA_FAILED'
)<UniversalSystemPageRequestType, any, Error>();
export const getQualisetDataRequest = (
  { day, week, systemGroup, system, isSilent, isProductionHours }: UniversalSystemPageRequestType,
  isRealTime: boolean = false
) => {
  const url = makeApiUrl('/api/system-page-report/qualiset');
  return apiAction({
    request: {
      url,
      headers: getRealTimeHeaders(isRealTime),
      params: {
        day,
        week,
        systemGroup,
        system,
        isProductionHours,
      },
    },
    logic: {
      onFailed: (error, dispatch) => {
        dispatch(getQualisetDataAC.failure(error));
      },
      onStarted: dispatch => {
        dispatch(getQualisetDataAC.request({ day, week, systemGroup, system, isSilent }));
      },
      onSuccess: (data, dispatch) => {
        dispatch(getQualisetDataAC.success({ data, systemGroup, system }));
      },
    },
  });
};

export const getImpressionsCardsAC = createAsyncAction(
  'GET_IMPRESSIONS_CARDS_STARTED',
  'GET_IMPRESSIONS_CARDS_SUCCESS',
  'GET_IMPRESSIONS_CARDS_FAILED'
)<UniversalSystemPageRequestType, any, Error>();
export const getImpressionCardsRequest = (
  { day, week, systemGroup, system, isSilent, isProductionHours }: UniversalSystemPageRequestType,
  isRealTime: boolean = false
) => {
  const url = makeApiUrl('/api/system-page-report/impressions-cards');
  return apiAction({
    request: {
      url,
      headers: getRealTimeHeaders(isRealTime),
      params: {
        day,
        week,
        systemGroup,
        system,
        isProductionHours,
      },
    },
    logic: {
      onFailed: (error, dispatch) => {
        dispatch(getImpressionsCardsAC.failure(error));
      },
      onStarted: dispatch => {
        dispatch(getImpressionsCardsAC.request({ day, week, systemGroup, system, isSilent }));
      },
      onSuccess: (data, dispatch) => {
        dispatch(getImpressionsCardsAC.success({ data, systemGroup }));
      },
    },
  });
};

export const getAvailabilityCardAC = createAsyncAction(
  'GET_AVAILABILITY_CARD_STARTED',
  'GET_AVAILABILITY_CARD_SUCCESS',
  'GET_AVAILABILITY_CARD_FAILED'
)<UniversalSystemPageRequestType, any, Error>();
export const getAvailabilityCardRequest = (
  {
    day,
    week,
    systemGroup,
    system,
    timeLineGranularity,
    isSilent,
    isProductionHours,
  }: UniversalSystemPageRequestType,
  isRealTime: boolean = false
) => {
  const url = makeApiUrl('/api/system-page-report/availability-and-timeline');
  return apiAction({
    request: {
      url,
      headers: getRealTimeHeaders(isRealTime),
      params: {
        day,
        week,
        systemGroup,
        system,
        timeLineGranularity,
        isProductionHours,
      },
    },
    logic: {
      onFailed: (error, dispatch) => {
        dispatch(getAvailabilityCardAC.failure(error));
      },
      onStarted: dispatch => {
        dispatch(getAvailabilityCardAC.request({ day, week, systemGroup, system, isSilent }));
      },
      onSuccess: (data, dispatch) => {
        if (week) {
          const { systemPageAvailabilityCardResponse, systemTimeLineWeekResponse } = data;

          const {
            systemTimeLineGraphResponse,
            systemTimeLineSummeryResponse,
          } = systemTimeLineWeekResponse;

          const {
            resultSummaryDataArray,
            resultTimeLineDataArray,
          } = mapTimeLineResponseToResultSortedObject(
            { systemTimeLineGraphResponse, systemTimeLineSummeryResponse },
            week
          );
          dispatch(
            getAvailabilityCardAC.success({
              systemPageAvailabilityResponse: systemPageAvailabilityCardResponse,
              resultSummaryDataArray,
              resultTimeLineDataArray,
              systemGroup,
              day,
              week,
            })
          );
        }

        if (day) {
          const DAY_VIEW_RECORDS_COUNT = 288;
          const { timeLineData } = data.systemTimeLineDayResponse;
          const type =
            timeLineData.length === DAY_VIEW_RECORDS_COUNT ? DayViewType.day : DayViewType.hour;
          const result = mapResposeToTimeLineDayArray(timeLineData, type);
          dispatch(
            getAvailabilityCardAC.success({
              systemPageAvailabilityResponse: data.systemPageAvailabilityCardResponse,
              systemTimeLineDayResponse: result,
              systemGroup,
              day,
              week,
            })
          );
        }
      },
    },
  });
};
export const getAchievementCardAC = createAsyncAction(
  'GET_ACHIEVEMENT_CARD_STARTED',
  'GET_ACHIEVEMENT_CARD_SUCCESS',
  'GET_ACHIEVEMENT_CARD_FAILED'
)<UniversalSystemPageRequestType, any, Error>();
export const getAchievementCardRequest = (
  {
    day,
    week,
    systemGroup,
    system,
    timeLineGranularity,
    isSilent,
    isProductionHours,
  }: UniversalSystemPageRequestType,
  isRealTime: boolean = false
) => {
  //*only serial number needed - it is always last element in system string
  const serialNumber = system?.split(' ').pop();
  const url = makeApiUrl('/api/system-page-report/achievement');
  return apiAction({
    request: {
      url,
      headers: getRealTimeHeaders(isRealTime),
      params: {
        day,
        week,
        systemGroup,
        system: serialNumber,
        timeLineGranularity,
        isProductionHours,
      },
    },
    logic: {
      onFailed: (error, dispatch) => {
        dispatch(getAchievementCardAC.failure(error));
      },
      onStarted: dispatch => {
        dispatch(getAchievementCardAC.request({ day, week, systemGroup, system, isSilent }));
      },
      onSuccess: (data, dispatch) => {
        dispatch(getAchievementCardAC.success(data));
      },
    },
  });
};
//*sorting model for timeline
export const timeLineSortingObject = [
  { name: 'Error', positionInOrder: 1, color: '#FF404F' },
  { name: 'Maintenance', positionInOrder: 2, color: '#FFAB2B' },
  { name: 'Idle', positionInOrder: 3, color: '#5E7EBF' },
  { name: 'Printing', positionInOrder: 4, color: '#1BB934' },
  { name: 'NotReporting', positionInOrder: 5, color: 'C5D0DE' },
  { name: 'Not Reporting', positionInOrder: 5, color: 'C5D0DE' },
];

const setAllNegativeValuesOfArrayToZero = (array: Array<{ colorName: string; colorValue }>) => {
  const result = array.map(el => {
    if (el.colorValue < 0) {
      return { ...el, colorValue: 0 };
    }
    if (el.colorValue === 'Infinity') {
      return { ...el, colorValue: 0 };
    } else {
      return el;
    }
  });
  return result;
};
